import type {
  NotificationCategory,
  NotificationDto,
  NotificationPopupsDto,
  NotificationPreferenceDto,
  PaginatedNotificationsDto,
  UnreadCountDto,
  UpdateNotificationPreferenceDto,
} from "@reactive-resume/dto";
import { axios } from "@/client/libs/axios";

/** Notification telle que renvoyée par l'API / poussée par le socket. */
export type NotificationItem = NotificationDto & {
  /** Métadonnée de diffusion (ne pas afficher de toast si true). */
  silent?: boolean;
};

export type NotificationFilters = {
  category?: NotificationCategory;
  unreadOnly?: boolean;
  limit?: number;
  cursor?: string;
};

export const fetchNotifications = async (filters: NotificationFilters = {}) => {
  const response = await axios.get<PaginatedNotificationsDto>("/notifications", {
    params: filters,
  });
  return response.data;
};

export const fetchUnreadCount = async () => {
  const response = await axios.get<UnreadCountDto>("/notifications/unread-count");
  return response.data;
};

export const fetchNotificationPopups = async () => {
  const response = await axios.get<NotificationPopupsDto>("/notifications/popups");
  return response.data.items as NotificationItem[];
};

export const markNotificationAsRead = async (id: string) => {
  const response = await axios.patch(`/notifications/${id}/read`);
  return response.data;
};

export const markAllNotificationsAsRead = async () => {
  const response = await axios.patch("/notifications/read-all");
  return response.data;
};

export const archiveNotification = async (id: string, undo = false) => {
  const response = await axios.patch(`/notifications/${id}/archive`, undefined, {
    params: undo ? { undo: "true" } : undefined,
  });
  return response.data;
};

export const deleteNotification = async (id: string) => {
  const response = await axios.delete(`/notifications/${id}`);
  return response.data;
};

export const fetchNotificationPreferences = async () => {
  const response = await axios.get<{ preferences: NotificationPreferenceDto[] }>(
    "/notifications/preferences",
  );
  return response.data.preferences;
};

export const updateNotificationPreference = async (
  category: NotificationCategory,
  data: UpdateNotificationPreferenceDto,
) => {
  const response = await axios.put<{ preference: NotificationPreferenceDto }>(
    `/notifications/preferences/${category}`,
    data,
  );
  return response.data.preference;
};

/** Config Web Push (Phase 1) — clé publique VAPID. */
export const fetchPushConfig = async () => {
  const response = await axios.get<{ publicKey: string; supported: boolean }>(
    "/notifications/push/config",
  );
  return response.data;
};

/** Lecture d'une notification complète (ouvre = marque comme lue côté serveur). */
export const fetchNotification = async (id: string) => {
  const response = await axios.get<NotificationItem>(`/notifications/${id}`);
  return response.data;
};

/**
 * FILES D'ATTENTE OPS (admin) — agrégat « ce qui exige une action maintenant ».
 * Un seul appel alimente le widget « À traiter » et les badges de la sidebar :
 * retraits à approuver, demandes d'aide non traitées, campagnes en envoi,
 * alertes admin non lues de l'opérateur connecté.
 */
export type AdminQueues = {
  withdrawals: {
    pendingCount: number;
    pendingAmount: number;
    oldestDemandDate: string | null;
  };
  help: {
    openCount: number;
    unansweredCount: number;
    oldest: { id: string; code: string; subject: string; createdAt: string } | null;
  };
  campaigns: { sendingCount: number };
  alerts: { unreadCount: number };
};

export const fetchAdminQueues = async () => {
  const response = await axios.get<AdminQueues>("/notifications/admin/queues");
  return response.data;
};
